import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  ArrowUpRight,
  Clock,
  Download,
  Eye,
  Heart,
  Loader2,
  MapPin,
  RotateCcw,
  Star,
  Truck,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import ChaafoLogo from "../../Images/chaafo-logo.svg";
import NoCafe from"../../Images/Restaurant_Logo_Placeholder.webp";
import useDriverTracker from "../../Services/usedriverTracker";
import { useGlobalContext } from "../../Context/GlobalContext";
import useAddress from "../../Services/useAddress";
import { useRestaurantList } from "../../Services/useRestaurantList";
import useOrders from "../../Services/useOrders";

const normalizeString = (value) => String(value ?? "").trim();

const normalizeValue = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replaceAll("_", " ");
};

const isDeliveredOrder = (order) => {
  const status = normalizeValue(
    order?.status || order?.order_status || order?.orderStatus,
  );

  const deliveryStatus = normalizeValue(order?.delivery_partner_status);

  return (
    status.includes("completed") ||
    status.includes("delivered") ||
    deliveryStatus.includes("successful") ||
    deliveryStatus.includes("delivered")
  );
};

const safelyParseJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getAddressText = (value) => {
  if (!value) {
    return "";
  }

  // =====================================================
  // NORMAL STRING
  // =====================================================

  if (typeof value === "string") {
    const cleanValue = value.trim();

    if (!cleanValue) {
      return "";
    }

    const parsedValue = safelyParseJson(cleanValue);

    if (parsedValue && typeof parsedValue === "object") {
      return getAddressText(parsedValue?.values || parsedValue);
    }

    return cleanValue;
  }

  // =====================================================
  // OBJECT ADDRESS
  // =====================================================

  if (typeof value === "object") {
    const addressObject = value?.values || value;

    const parts = [
      addressObject?.house_number,
      addressObject?.houseNumber,

      addressObject?.flat_number,
      addressObject?.flatNumber,

      addressObject?.tower_number,
      addressObject?.towerNumber,

      addressObject?.floor_number,
      addressObject?.floorNumber,

      addressObject?.address,
      addressObject?.address_line,
      addressObject?.addressLine,

      addressObject?.building_name,
      addressObject?.buildingName,

      addressObject?.landmark_area,
      addressObject?.landmark,

      addressObject?.area,
      addressObject?.locality,

      addressObject?.city,
      addressObject?.district,
      addressObject?.state,

      addressObject?.in_pin_code,
      addressObject?.pinCode,
      addressObject?.pincode,
    ]
      .map((item) => (typeof item === "string" ? normalizeString(item) : ""))
      .filter(Boolean);

    return [...new Set(parts)].join(", ");
  }

  return "";
};

const formatCustomerMobile = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");

  if (!digits) return "";
  return digits.length === 10 ? `91${digits}` : digits;
};

const getStoredValue = (...keys) => {
  if (typeof window === "undefined") return "";

  for (const key of keys) {
    const sessionValue = normalizeString(sessionStorage.getItem(key));
    if (sessionValue) return sessionValue;

    const localValue = normalizeString(localStorage.getItem(key));
    if (localValue) return localValue;
  }

  return "";
};

const formatAmount = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) return "0";

  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatOrderDate = (value) => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) return "Date unavailable";

  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) return normalizedValue;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const getOrderStatus = (order) =>
  normalizeString(
    order?.status || order?.order_status || order?.orderStatus || "Processing",
  );

const getOrderStatusClass = (status) => {
  const normalizedStatus = normalizeString(status).toLowerCase();

  if (
    normalizedStatus.includes("deliver") ||
    normalizedStatus.includes("complete")
  ) {
    return "bg-green-100 text-green-600";
  }

  if (
    normalizedStatus.includes("cancel") ||
    normalizedStatus.includes("reject") ||
    normalizedStatus.includes("not-delivered")
  ) {
    return "bg-red-100 text-red-600";
  }

  if (normalizedStatus.includes("refund")) {
    return "bg-purple-100 text-purple-600";
  }

  return "bg-[#d8eafe] text-[#1d4ed8]";
};

const isProcessingOrder = (order) => {
  const status = getOrderStatus(order).toLowerCase();

  return (
    order?.processing === true ||
    order?.processing === 1 ||
    order?.processing === "1" ||
    normalizeString(order?.processing).toUpperCase() === "Y" ||
    status.includes("processing") ||
    status.includes("preparing") ||
    status.includes("accepted") ||
    status.includes("received") ||
    status.includes("assigned") ||
    status.includes("ready") ||
    status.includes("picked") ||
    status.includes("dispatch")
  );
};

const getOrderItemCount = (order) => {
  if (Array.isArray(order?.items)) return order.items.length;

  const directCount = Number(
    order?.item_count || order?.itemCount || order?.total_items,
  );

  return Number.isFinite(directCount) ? directCount : 0;
};

const getOrderImage = (order) =>
  normalizeString(
    order?.image ||
      order?.restaurant_image ||
      order?.restaurantImage ||
      order?.organization_logo ||
      order?.brand_image,
  ) || NoCafe;

const removeDuplicateAddresses = (addresses = []) => {
  const addressMap = new Map();

  addresses.forEach((item) => {
    const address = normalizeString(item?.address);
    const key = address.toLowerCase();

    if (!address || addressMap.has(key)) return;

    addressMap.set(key, {
      ...item,
      address,
      favourite:
        normalizeString(item?.favourite) ||
        normalizeString(item?.favorite) ||
        "Saved Address",
    });
  });

  return Array.from(addressMap.values()).sort(
    (firstAddress, secondAddress) =>
      Number(Boolean(secondAddress?.isDefault)) -
      Number(Boolean(firstAddress?.isDefault)),
  );
};

const SectionLoader = ({ text }) => (
  <div className="flex min-h-[150px] flex-col items-center justify-center gap-3 text-gray-400">
    <Loader2 size={25} className="animate-spin" />
    <p className="text-sm">{text}</p>
  </div>
);

const SectionEmpty = ({ icon: Icon, text }) => (
  <div className="flex min-h-[150px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 text-center">
    <Icon size={25} className="text-gray-400" />
    <p className="mt-3 text-sm text-gray-500">{text}</p>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();

  const { user, authLoading, selectRestaurant, selectedRestaurant } =
    useGlobalContext();

  const { getCustomerList } = useAddress();

const {
  likedRestaurantDetails,
  likedRestaurantLoading,
  getLikeRestaurantDetails,
} = useRestaurantList();

const {
  orders,
  loading: ordersLoading,
  getUserCatalogOrders,
  getOrderReceiptDetails,
} = useOrders();

const { getDriverTrackingUrl } = useDriverTracker();

  const customerId = normalizeString(
    user?.user_code ||
      user?.userCode ||
      user?.customerId ||
      user?.customer_id ||
      getStoredValue("user_code", "userCode", "customerId", "customer_id"),
  );

  const customerMobile = formatCustomerMobile(
    user?.phone ||
      user?.number ||
      user?.mobile ||
      getStoredValue("phone", "number", "mobile", "customerMobile"),
  );

 const restaurantUserId = "A1001";

  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState("");
  const [favouritesError, setFavouritesError] = useState("");
  const [ordersError, setOrdersError] = useState("");
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState("");

  const loadAddresses = useCallback(async () => {
    if (!customerMobile || !restaurantUserId) {
      setAddresses([]);
      setAddressesError(
        !restaurantUserId
          ? "Select a restaurant to load delivery addresses."
          : "Customer mobile number is not available.",
      );
      return;
    }

    setAddressesLoading(true);
    setAddressesError("");

    try {
      const result = await getCustomerList({
        digit: customerMobile,
        userid: restaurantUserId,
      });

      const customers = Array.isArray(result?.customers)
        ? result.customers
        : result?.customer
          ? [result.customer]
          : [];

      const customerAddresses = [];

      const selectedAddressDetails = JSON.parse(
        sessionStorage.getItem("selectedDeliveryAddressDetails") ||
          localStorage.getItem("selectedDeliveryAddressDetails") ||
          "null",
      );

      const selectedAddressText = normalizeString(
        selectedAddressDetails?.address ||
          sessionStorage.getItem("selectedDeliveryAddress") ||
          localStorage.getItem("selectedDeliveryAddress"),
      );

      const selectedAddressTag = normalizeString(selectedAddressDetails?.tag);

      customers.forEach((customer, customerIndex) => {
      const defaultAddressRaw =
        customer?.defaultAddress || customer?.default_address;

      const defaultAddress = getAddressText(defaultAddressRaw);

      const defaultAddressObject =
        defaultAddressRaw && typeof defaultAddressRaw === "object"
          ? defaultAddressRaw?.values || defaultAddressRaw
          : {};

      if (defaultAddress) {
        customerAddresses.push({
          id:
            defaultAddressObject?.id ||
            defaultAddressObject?.pk_location_id ||
            defaultAddressObject?.location_id ||
            `default-${customer?.id || customerIndex}`,

          address: defaultAddress,

          favourite:
            normalizeString(
              defaultAddressObject?.tag ||
                defaultAddressObject?.address_tag ||
                customer?.tag,
            ) || "Delivery Address",

          isDefault: true,
        });
      }

        const rawAddresses = Array.isArray(customer?.address)
          ? customer.address
          : Array.isArray(customer?.addresses)
            ? customer.addresses
            : customer?.address
              ? [customer.address]
              : [];

        rawAddresses.forEach((addressItem, addressIndex) => {
          const address = normalizeString(
            typeof addressItem === "string"
              ? addressItem
              : addressItem?.address ||
                  addressItem?.full_address ||
                  addressItem?.delivery_address,
          );

          if (!address) return;

          customerAddresses.push({
            id:
              addressItem?.id ||
              `customer-${customer?.id || customerIndex}-${addressIndex}`,
            address,
            favourite:
              normalizeString(addressItem?.tag) ||
              normalizeString(customer?.tag) ||
              "Delivery Address",
            isDefault: Boolean(addressItem?.isDefault),
          });
        });
      });

const apiAddresses = removeDuplicateAddresses(customerAddresses);

const savedAddresses = safelyParseJson(
  sessionStorage.getItem("savedAddresses") ||
    localStorage.getItem("savedAddresses"),
);

const cachedAddresses = Array.isArray(savedAddresses)
  ? removeDuplicateAddresses(savedAddresses)
  : [];

const finalAddresses = apiAddresses.length > 0 ? apiAddresses : cachedAddresses;

setAddresses(finalAddresses.slice(0, 3));

if (apiAddresses.length > 0) {
  sessionStorage.setItem("savedAddresses", JSON.stringify(apiAddresses));

  localStorage.setItem("savedAddresses", JSON.stringify(apiAddresses));
}
    } catch (error) {
      setAddresses([]);
      setAddressesError(error?.message || "Unable to load saved addresses.");
    } finally {
      setAddressesLoading(false);
    }
  }, [customerMobile, restaurantUserId, getCustomerList]);

  const loadFavourites = useCallback(async () => {
    if (!customerId) {
      setFavouritesError("Customer ID is not available.");
      return;
    }

    setFavouritesError("");

    const result = await getLikeRestaurantDetails({
      customerId,
      latitude:
        user?.latitude ||
        localStorage.getItem("latitude") ||
        sessionStorage.getItem("latitude") ||
        "",
      longitude:
        user?.longitude ||
        localStorage.getItem("longitude") ||
        sessionStorage.getItem("longitude") ||
        "",
      range: "20",
    });

    if (!result?.success) {
      setFavouritesError(result?.message || "Unable to load favourites.");
    }
  }, [customerId, getLikeRestaurantDetails]);

  const loadOrders = useCallback(async () => {
    if (!customerId && !customerMobile) {
      setOrdersError("Customer information is not available.");
      return;
    }

    setOrdersError("");

    const result = await getUserCatalogOrders({
      userid: customerId,
      custMobile: customerMobile,
      pageIndex: 1,
      recordPerPage: 10,
    });

    if (!result || String(result?.responseCode) !== "00") {
      setOrdersError(
        result?.responseMessage || "Unable to load recent orders.",
      );
    }
  }, [customerId, customerMobile, getUserCatalogOrders]);

  useEffect(() => {
    if (authLoading) return;

    loadAddresses();
    loadFavourites();
    loadOrders();
  }, [authLoading, loadAddresses, loadFavourites, loadOrders]);

  useEffect(() => {
    const handleSavedAddressesUpdated = () => {
      loadAddresses();
    };

    window.addEventListener(
      "savedAddressesUpdated",
      handleSavedAddressesUpdated,
    );

    return () => {
      window.removeEventListener(
        "savedAddressesUpdated",
        handleSavedAddressesUpdated,
      );
    };
  }, [loadAddresses]);

  const visibleAddresses = useMemo(() => addresses.slice(0, 3), [addresses]);

  const visibleFavourites = useMemo(() => {
    return likedRestaurantDetails
      .filter(
        (restaurant) =>
          normalizeString(restaurant?.likeStatus).toUpperCase() === "Y",
      )
      .slice(0, 2);
  }, [likedRestaurantDetails]);

const visibleOrders = useMemo(() => {
  return [...orders]
    .sort((firstOrder, secondOrder) => {
      const firstDate = new Date(firstOrder?.order_date || 0).getTime();

      const secondDate = new Date(secondOrder?.order_date || 0).getTime();

      return secondDate - firstDate;
    })
    .slice(0, 4);
}, [orders]);

  const handleFavouriteClick = (restaurant) => {
    const userId = normalizeString(restaurant?.userId);

    if (!userId) return;

  const rawRestaurant = restaurant?.raw || {};

  const selectedRestaurantData = {
    userId,

    name:
      restaurant?.organizationName ||
      rawRestaurant?.name ||
      rawRestaurant?.restaurant_name ||
      "Restaurant",

    image:
      restaurant?.brandImage ||
      restaurant?.imageUrl ||
      restaurant?.organizationLogo ||
      rawRestaurant?.image ||
      rawRestaurant?.restaurant_image ||
      "",

    address: String(restaurant?.address || rawRestaurant?.address || "").trim(),

    phone: String(
      restaurant?.phone ||
        rawRestaurant?.phone ||
        rawRestaurant?.restaurant_phone ||
        "",
    ).trim(),

    city: String(restaurant?.city || rawRestaurant?.city || "").trim(),

    state: String(restaurant?.state || rawRestaurant?.state || "").trim(),

    latitude: restaurant?.latitude ?? rawRestaurant?.latitude ?? "",

    longitude: restaurant?.longitude ?? rawRestaurant?.longitude ?? "",

    servingType: String(
      restaurant?.servingType ||
        rawRestaurant?.servingType ||
        rawRestaurant?.serving_type ||
        "",
    )
      .trim()
      .toUpperCase(),

    startingPrice: Number.isFinite(
      Number(
        restaurant?.startingPrice ??
          rawRestaurant?.startingPrice ??
          rawRestaurant?.starting_price,
      ),
    )
      ? Number(
          restaurant?.startingPrice ??
            rawRestaurant?.startingPrice ??
            rawRestaurant?.starting_price,
        )
      : null,
  };

    selectRestaurant?.(selectedRestaurantData);

    navigate(`/restaurant-details/${userId}`, {
      state: {
        restaurant: selectedRestaurantData,
      },
    });
  };

  const openOrdersPage = (order, action = "") => {
    navigate("/account/orders", {
      state: {
        selectedOrderId: order?.orderid || order?.id || "",
        action,
      },
    });
  };

  const handleTrackOrder = useCallback(
    async (order) => {
      const orderId = String(
        order?.orderid || order?.orderId || order?.order_id || "",
      ).trim();

      if (!orderId) {
        console.error("Order ID is missing");
        return;
      }

      // User click ke time hi tab open karo
      // taaki browser popup block na kare
      const trackingTab = window.open("about:blank", "_blank");

      if (!trackingTab) {
        console.error("Popup was blocked by the browser.");
        return;
      }

      try {
        const trackingUrl = await getDriverTrackingUrl({
          orderid: orderId,
        });

        trackingTab.location.href = trackingUrl;
      } catch (error) {
        console.error("Unable to track order:", error);

        trackingTab.close();
      }
    },
    [getDriverTrackingUrl],
  );

  const handleInvoiceDownload = useCallback(
    async (order) => {
      const orderId = String(
        order?.orderid || order?.orderId || order?.order_id || "",
      ).trim();

      if (!orderId) {
        console.error("Invoice order ID missing:", order);
        return;
      }

      if (downloadingInvoiceId) {
        return;
      }

      // User click ke time new tab open karo
      // taaki popup browser block na kare
      const invoiceTab = window.open("about:blank", "_blank");

      if (!invoiceTab) {
        console.error("Invoice tab was blocked by the browser.");
        return;
      }

      try {
        setDownloadingInvoiceId(orderId);

        const response = await getOrderReceiptDetails({
          orderid: orderId,
          platform: "WEB",
        });

        console.log("DASHBOARD DOWNLOAD RECEIPT RESPONSE:", response);

        const receiptResponse =
          response?.response ??
          response?.data?.response ??
          response?.data ??
          null;

        const receipt = Array.isArray(receiptResponse)
          ? receiptResponse[0] || null
          : receiptResponse;

        if (!receipt) {
          throw new Error(
            response?.responseMessage || "Invoice data not found.",
          );
        }

        // New tab ke liye receipt temporarily save
        localStorage.setItem(
          `invoice-receipt-${orderId}`,
          JSON.stringify(receipt),
        );

        // Current dashboard wahi rahega
        // Receipt new tab me open hogi
        invoiceTab.location.href = `/order-summary?orderid=${encodeURIComponent(
          orderId,
        )}&download=1`;
      } catch (error) {
        console.error("Dashboard invoice download error:", error);

        invoiceTab.close();
      } finally {
        setDownloadingInvoiceId("");
      }
    },
    [downloadingInvoiceId, getOrderReceiptDetails],
  );

 const handleReorder = useCallback(
   (order) => {
     const restaurantId = String(
       order?.fk_user_id || order?.user_id || order?.restaurant_id || "",
     ).trim();

     if (!restaurantId) {
       console.error("Restaurant ID is missing for reorder:", order);
       return;
     }

     navigate(`/restaurant-details/${restaurantId}`, {
       state: {
         reorderOrder: order,
       },
     });
   },
   [navigate],
 );

  return (
    <div className="rounded-3xl border border-gray-100 bg-white h-auto flex flex-col overflow-hidden shadow-sm">
      <div className="shrink-0 border-b border-gray-100 bg-white px-4 py-5 sm:px-6">
        <h2 className="text-2xl font-semibold text-gray-800 sm:text-[28px]">
          My Account
        </h2>
      </div>

      <div className="overflow-y-visible hide-scrollbar px-4 py-5 sm:px-6">
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-1">
          <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Saved Addresses</h3>

              <button
                type="button"
                onClick={() => navigate("/account/address")}
                className="
        flex
        items-center
        gap-1.5
        text-sm
        font-medium
        text-[#1d4ed8]
        transition
        hover:text-[#103aad]
      "
              >
                <ArrowUpRight size={16} />
                See all
              </button>
            </div>

            {addressesLoading ? (
              <SectionLoader text="Loading addresses..." />
            ) : visibleAddresses.length > 0 ? (
              <div
                className={`
    grid
    grid-cols-1
    gap-3

    ${
      visibleAddresses.length === 2
        ? "sm:grid-cols-2"
        : visibleAddresses.length >= 3
          ? "sm:grid-cols-2 lg:grid-cols-3"
          : "grid-cols-1"
    }
  `}
              >
                {visibleAddresses.map((item, index) => (
                  <button
                    key={item?.id || `${item?.address}-${index}`}
                    type="button"
                    onClick={() => navigate("/account/address")}
                    className={`
  group
  min-h-[150px]
  w-full
  flex-col
  rounded-2xl
  border
  border-gray-200
  bg-white
  p-4
  text-left
  transition-all
  duration-200

  hover:-translate-y-0.5
  hover:border-blue-200
  hover:shadow-md

  sm:p-5

  ${index >= 2 ? "flex sm:hidden xl:flex" : "flex"}
`}
                  >
                    {/* TOP */}
                    <div className="flex w-full items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <div
                          className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center
                rounded-full
                bg-blue-50
                text-[#1d4ed8]
              "
                        >
                          <MapPin size={17} />
                        </div>

                        <span className="min-w-0 truncate text-sm font-semibold capitalize text-gray-800">
                          {item?.favourite || "Saved Address"}
                        </span>
                      </div>

                      {item?.isDefault && (
                        <span
                          className="
                shrink-0
                rounded-full
                bg-[#d8eafe]
                px-2
                py-1
                text-[9px]
                font-semibold
                text-[#1d4ed8]
              "
                        >
                          Default
                        </span>
                      )}
                    </div>

                    {/* ADDRESS */}
                    <p
                      className="
            mt-3
            line-clamp-3
            text-xs
            leading-5
            text-gray-500
          "
                    >
                      {item?.address}
                    </p>

                    {/* BOTTOM */}
                  </button>
                ))}
              </div>
            ) : (
              <SectionEmpty
                icon={MapPin}
                text={addressesError || "No saved addresses found."}
              />
            )}
          </div>
        </div>

        <div className="mt-4 rounded-3xl border border-gray-200 bg-white p-4 sm:p-5">
          {/* ===================================================== */}
          {/* HEADER */}
          {/* ===================================================== */}

          <div className="mb-5 flex items-center justify-between gap-3">
            <h3 className="text-xl font-semibold text-gray-900">
              Recent Orders
            </h3>

            <button
              type="button"
              onClick={() => navigate("/account/orders")}
              className="
        flex
        items-center
        gap-1.5
        text-sm
        font-medium
        text-[#1d4ed8]
        transition
        hover:text-[#103aad]
      "
            >
              <ArrowUpRight size={16} />
              See all
            </button>
          </div>

          {/* ===================================================== */}
          {/* CONTENT */}
          {/* ===================================================== */}

          {ordersLoading ? (
            <SectionLoader text="Loading recent orders..." />
          ) : visibleOrders.length > 0 ? (
            <div
              className="
        grid
        grid-cols-1
        gap-4

        md:grid-cols-2
        md:gap-5
      "
            >
              {visibleOrders.map((item, index) => {
                const orderStatus = getOrderStatus(item);

                const itemCount = getOrderItemCount(item);

                const restaurantName =
                  item?.organization_name ||
                  item?.restaurant_name ||
                  item?.shop_name ||
                  "Restaurant";

                const orderId =
                  item?.orderid || item?.daily_order_id || item?.id || "-";

                const amount =
                  item?.pay_amount ||
                  item?.total_amount ||
                  item?.grand_total ||
                  item?.total_order_price ||
                  item?.amount ||
                  0;

                const orderType = String(
                  item?.orderType ||
                    item?.order_type ||
                    item?.orderTypeName ||
                    "",
                )
                  .trim()
                  .toLowerCase();

                const orderTypeText =
                  orderType === "takeaway"
                    ? "Takeaway order"
                    : orderType === "dining" ||
                        orderType === "dinein" ||
                        orderType === "dine-in"
                      ? "Dining order"
                      : orderType === "delivery"
                        ? "Delivery order"
                        : orderType
                          ? `${orderType
                              .charAt(0)
                              .toUpperCase()}${orderType.slice(1)} order`
                          : "Order";

                const normalizedStatus = String(orderStatus || "")
                  .trim()
                  .replaceAll("_", " ");

                return (
                  <article
                    key={item?.orderid || item?.id || index}
                    className="
        overflow-hidden
        rounded-[15px]
        border
        border-[#dce3eb]
        bg-white
        shadow-[0_2px_8px_rgba(15,23,42,0.025)]
        transition
        hover:border-[#ccd6e2]
        hover:shadow-[0_6px_16px_rgba(15,23,42,0.05)]
      "
                  >
                    {/* =============================================== */}
                    {/* TOP */}
                    {/* =============================================== */}

                    <div className="flex items-start justify-between gap-3 px-4 py-4 sm:px-5">
                      {/* LEFT */}

                      <div className="flex min-w-0 items-center gap-3">
                        {/* RESTAURANT IMAGE */}
                        <div
                          className="
    h-[44px]
    w-[44px]
    shrink-0
    overflow-hidden
    rounded-[11px]
    border
    border-[#e5e7eb]
    bg-[#f8fafc]
  "
                        >
                          <img
                            src={getOrderImage(item)}
                            alt={restaurantName}
                            loading="lazy"
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = NoCafe;
                            }}
                            className="
      h-full
      w-full
      object-cover
    "
                          />
                        </div>

                        {/* NAME + DATE */}

                        <div className="min-w-0">
                          <h4
                            title={restaurantName}
                            className="
                truncate
                text-[15px]
                font-bold
                leading-5
                text-[#111827]

                sm:text-[16px]
              "
                          >
                            {restaurantName}
                          </h4>

                          <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[#8794a8]">
                            <Clock
                              size={13}
                              strokeWidth={1.8}
                              className="shrink-0"
                            />

                            <span className="truncate text-[10px] sm:text-[11px]">
                              {formatOrderDate(
                                item?.order_date ||
                                  item?.created_on ||
                                  item?.created_at,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* STATUS */}

                      <span
                        className={`
            inline-flex
            max-w-[145px]
            shrink-0
            items-center
            gap-1.5

            rounded-full

            px-2.5
            py-1.5

            text-[9px]
            font-semibold
            capitalize

            sm:px-3
            sm:text-[10px]

            ${getOrderStatusClass(orderStatus)}
          `}
                      >
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" />

                        <span className="truncate">{normalizedStatus}</span>
                      </span>
                    </div>

                    {/* =============================================== */}
                    {/* ORDER ID */}
                    {/* =============================================== */}

                    <div
                      className="
          flex
          items-center
          justify-between
          gap-3

          border-t
          border-[#e7ebf0]

          bg-[#fbfcfe]

          px-4
          py-3

          sm:px-5
        "
                    >
                      <span
                        className="
            text-[10px]
            font-medium
            uppercase
            tracking-[0.04em]
            text-[#76859a]

            sm:text-[11px]
          "
                      >
                        Order ID
                      </span>

                      <span
                        title={String(orderId)}
                        className="
            min-w-0
            truncate
            text-right

            text-[11px]
            font-bold
            text-[#172033]

            sm:text-[12px]
          "
                      >
                        #{orderId}
                      </span>
                    </div>

                    {/* =============================================== */}
                    {/* ITEM COUNT + AMOUNT */}
                    {/* =============================================== */}

                    <div className="grid grid-cols-2 border-[#e7ebf0]">
                      {/* ITEMS */}

                      <div
                        className="
            flex
            items-center

            px-4
            py-4

            sm:px-5
          "
                      >
                        <span
                          className="
              text-[13px]
              font-bold
              text-[#111827]

              sm:text-[14px]
            "
                        >
                          {itemCount} {itemCount === 1 ? "Item" : "Items"}
                        </span>
                      </div>

                      {/* AMOUNT */}

                      <div
                        className="
            flex
            items-center
            justify-end

            border-[#e7ebf0]

            px-4
            py-4

            sm:px-5
          "
                      >
                        <span
                          className="
              text-[13px]
              font-bold
              text-[#111827]

              sm:text-[14px]
            "
                        >
                          ₹ {formatAmount(amount)}
                        </span>
                      </div>
                    </div>

                    {/* =============================================== */}
                    {/* BOTTOM ACTION BAR */}
                    {/* =============================================== */}

                    <div
                      className="
          flex
          min-h-[56px]
          items-center
          justify-between
          gap-3

          border-t
          border-[#e7ebf0]

          px-4
          py-2

          sm:px-5
        "
                    >
                      {/* LEFT ACTIONS */}

                      <div className="flex items-center gap-1">
                        {/* VIEW */}

                        <button
                          type="button"
                          title="View Details"
                          aria-label="View Details"
                          onClick={() => openOrdersPage(item, "view")}
                          className="
              flex
              h-9
              w-9
              items-center
              justify-center

              rounded-full

              text-[#53657c]

              transition

              hover:bg-blue-50
              hover:text-[#2454e6]

              active:scale-95
            "
                        >
                          <Eye size={17} strokeWidth={1.8} />
                        </button>

                        {/* DOWNLOAD */}

                        <button
                          type="button"
                          title="Download Invoice"
                          aria-label="Download Invoice"
                          disabled={Boolean(downloadingInvoiceId)}
                          onClick={() => handleInvoiceDownload(item)}
                          className="
              flex
              h-9
              w-9
              items-center
              justify-center

              rounded-full

              text-[#53657c]

              transition

              hover:bg-blue-50
              hover:text-[#2454e6]

              active:scale-95
            "
                        >
                          {downloadingInvoiceId ===
                          String(
                            item?.orderid ||
                              item?.orderId ||
                              item?.order_id ||
                              "",
                          ) ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} strokeWidth={1.8} />
                          )}
                        </button>
                      </div>

                      {/* RIGHT ACTION */}
                      <div className="ml-auto flex shrink-0 items-center justify-end">
                        {isDeliveredOrder(item) ? (
                          <button
                            type="button"
                            onClick={() => handleReorder(item)}
                            className="
        inline-flex
        h-9
        items-center
        justify-center
        gap-1.5
        rounded-[9px]
        bg-[#315bea]
        px-3.5
        text-[10px]
        font-semibold
        text-white
        shadow-[0_4px_10px_rgba(49,91,234,0.18)]
        transition
        hover:bg-[#244bd0]
        active:scale-[0.98]
        sm:px-4
        sm:text-[11px]
      "
                          >
                            <RotateCcw size={14} strokeWidth={2} />
                            Reorder
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleTrackOrder(item)}
                            className="
        inline-flex
        h-9
        items-center
        justify-center
        gap-1.5
        rounded-[9px]
        bg-[#315bea]
        px-3.5
        text-[10px]
        font-semibold
        text-white
        shadow-[0_4px_10px_rgba(49,91,234,0.18)]
        transition
        hover:bg-[#244bd0]
        active:scale-[0.98]
        sm:px-4
        sm:text-[11px]
      "
                          >
                            <MapPin size={14} strokeWidth={2} />
                            Track Order
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <SectionEmpty
              icon={Clock}
              text={ordersError || "No recent orders found."}
            />
          )}
        </div>
      </div>
    </div>
  );
}

